import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Wrench, Snowflake, BatteryCharging, Disc, PaintBucket, Sparkles,
  CircleDot, Compass, Droplet, ShieldCheck, AlertTriangle, Square,
  ChevronRight, MapPin, Phone, MessageCircle, Star, Clock, IndianRupee,
} from "lucide-react";
import {
  cities, services, brands, findCity, findService, findBrand,
  findLocality, findModel, labelOf, findIntro,
} from "../../utils/seoData";
import { useSeoHead } from "../../utils/useSeoHead";
import CarServiceHeader from "../../components/carservice/CarServiceHeader";
import CarServiceFooter from "../../components/carservice/CarServiceFooter";

const ICONS = {
  Wrench, Snowflake, BatteryCharging, Disc, PaintBucket, Sparkles,
  CircleDot, Compass, Droplet, ShieldCheck, AlertTriangle, Square,
};

const SITE_ORIGIN = "https://www.wisedrive.com";

/**
 * One template — six tiers.
 *   /car-service/{city}
 *   /car-service/{city}/{locality}
 *   /car-service/{city}/{service}
 *   /car-service/{city}/{service}/{locality}
 *   /car-service/{city}/{service}/{brand}
 *   /car-service/{city}/{service}/{brand}/{model}
 *
 * Resolves the URL params, picks the right shape of content (H1, breadcrumbs,
 * internal links, schema), and lets the existing CarService booking funnel
 * handle conversion via deep-link params.
 */
const CarServiceSEOPage = () => {
  const params = useParams();
  const resolved = useMemo(() => resolveTier(params), [params]);

  // ALL hooks must run unconditionally — useSeoHead with safe defaults if resolved is null.
  const canonical = resolved ? `${SITE_ORIGIN}${resolved.canonicalPath}` : SITE_ORIGIN;
  useSeoHead({
    title: resolved?.title || "Page not found · Wisedrive",
    description: resolved?.description,
    canonical,
    robots: resolved ? "index,follow" : "noindex,follow",
    jsonLd: resolved
      ? buildJsonLd({
          city: resolved.city,
          locality: resolved.locality,
          service: resolved.service,
          brand: resolved.brand,
          model: resolved.model,
          canonical,
          h1: resolved.h1,
          breadcrumbs: resolved.breadcrumbs,
        })
      : null,
  });

  // 404 — unknown city/service/brand/model
  if (!resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Page not found</h1>
          <p className="text-slate-600 mb-6">We don't have a page for that yet.</p>
          <Link to="/carservice" className="inline-block px-5 py-3 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">
            Visit Car Service
          </Link>
        </div>
      </div>
    );
  }

  const { tier, city, locality, service, brand, model, h1, description, breadcrumbs, neighbors } = resolved;
  const aiIntro = findIntro(resolved.canonicalPath);

  const ServiceIcon = service ? (ICONS[service.icon] || Wrench) : Wrench;
  const fromPrice = computePrice(service, brand);
  const ctaQs = buildCtaQs({ city, locality, service, brand, model });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <CarServiceHeader />

      {/* Hero */}
      <header className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white pt-10 pb-12 sm:pt-14 sm:pb-16 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <Breadcrumbs items={breadcrumbs} />

          <h1
            className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
            style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
            data-testid="seo-h1"
          >
            {h1}
          </h1>

          <p className="mt-4 text-base sm:text-lg text-blue-100/90 max-w-3xl leading-relaxed">
            {description}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            <Pill icon={Star}        text="4.8★ · 3,200+ reviews" />
            <Pill icon={Clock}       text={service?.duration || "60-90 min"} />
            <Pill icon={ShieldCheck} text="1,000 km warranty" />
            <Pill icon={MapPin}      text={locality ? `Doorstep · ${locality.name}` : "Doorstep · all areas"} />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/carservice${ctaQs}`}
              data-testid="seo-cta-quote"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm sm:text-base shadow-lg transition-all"
            >
              Get Instant Quote
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href={`https://wa.me/919905129146?text=${encodeURIComponent(buildWhatsAppText({ city, locality, service, brand, model }))}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="seo-cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm sm:text-base transition-all"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp +91 99051 29146
            </a>
          </div>
        </div>
      </header>

      {/* AI-generated unique intro paragraph — when present, this is the page's
          primary thin-content defense. Hand-quality 80-word paragraph keyed by
          canonical path; see /app/scripts/generate_seo_intros.py. */}
      {aiIntro && (
        <section className="px-5 sm:px-8 pt-8 sm:pt-12">
          <div className="max-w-3xl mx-auto">
            <p
              className="text-base sm:text-lg text-slate-700 leading-relaxed"
              data-testid="seo-ai-intro"
            >
              {aiIntro}
            </p>
          </div>
        </section>
      )}

      {/* Service detail card */}
      {service && (
        <section className="px-5 sm:px-8 py-10 sm:py-12">
          <div className="max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-slate-50/50 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-blue-100 inline-flex items-center justify-center">
                <ServiceIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-600 mb-1">
                  {service.category}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  {brand && model
                    ? `${brand.name} ${labelOf(model.slug)} ${service.name}`
                    : brand
                    ? `${brand.name} ${service.name}`
                    : service.name}
                </h2>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  Genuine OEM parts, certified mechanics, transparent fixed pricing — no surprise bills.
                  Most {service.name.toLowerCase()} jobs done in {service.duration} at your doorstep
                  {locality ? ` in ${locality.name}` : ` across ${city.name}`}.
                </p>
                <div className="mt-4 flex flex-wrap gap-3 items-baseline">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Starts at</span>
                  <span className="text-2xl sm:text-3xl font-black text-emerald-600 tracking-tight">
                    <IndianRupee className="inline h-5 w-5 -mt-1.5" />
                    {fromPrice.toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs text-slate-500">· all-inclusive · taxes & parts included</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Wisedrive — value strip */}
      <section className="px-5 sm:px-8 py-10 sm:py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8 text-center">
            Why Wisedrive in {city.name}{locality ? `, ${locality.name}` : ""}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <v.icon className="h-6 w-6 text-blue-600 mb-3" />
                <div className="text-sm font-bold text-slate-900 mb-1">{v.title}</div>
                <div className="text-xs text-slate-600 leading-relaxed">{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking — neighbors */}
      {neighbors.length > 0 && (
        <section className="px-5 sm:px-8 py-10 sm:py-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-5">
              {tier === "city" && `Popular services in ${city.name}`}
              {tier === "city-locality" && `Services in ${locality.name}`}
              {tier === "city-service" && `${service.name} across ${city.name}`}
              {tier === "city-service-locality" && `Other models we service in ${locality.name}`}
              {tier === "city-service-brand" && `${brand.name} models we service in ${city.name}`}
              {tier === "city-service-brand-model" && `Related ${brand.name} services in ${city.name}`}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {neighbors.map((n) => (
                <Link
                  key={n.path}
                  to={n.path}
                  className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                  data-testid={`seo-neighbor-link-${n.path}`}
                >
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-700">
                    {n.label}
                  </div>
                  {n.sub && <div className="text-xs text-slate-500 mt-0.5">{n.sub}</div>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-5 sm:px-8 py-10 sm:py-12 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            {buildFaqs({ city, locality, service, brand, model }).map((q) => (
              <details key={q.q} className="group rounded-xl bg-white border border-slate-200 p-4">
                <summary className="cursor-pointer font-semibold text-slate-900 list-none flex items-start justify-between gap-3">
                  {q.q}
                  <ChevronRight className="h-4 w-4 mt-1 transition-transform group-open:rotate-90 shrink-0 text-slate-400" />
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{q.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-5 sm:px-8 py-12 bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Ready to book your {service?.name?.toLowerCase() || "service"} in {locality?.name || city.name}?
          </h2>
          <p className="text-white/90 mb-6">
            Get a fixed-price quote in 60 seconds. No registration, no commitment.
          </p>
          <Link
            to={`/carservice${ctaQs}`}
            data-testid="seo-cta-bottom"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-blue-700 font-bold shadow-lg hover:bg-blue-50 transition-all"
          >
            Get Instant Quote
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <CarServiceFooter />
    </div>
  );
};

/* ---------- helpers (kept in same file for one-template ergonomics) ---------- */

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "1,000 km warranty",  body: "Every job covered. No fine print, no exclusions on parts or labour." },
  { icon: Clock,       title: "60-min service",     body: "Most jobs done in an hour at your doorstep. No workshop wait." },
  { icon: IndianRupee, title: "Fixed pricing",      body: "All-inclusive quote upfront. Taxes, parts and labour. Zero surprise bills." },
  { icon: Star,        title: "4.8★ · 3,200 reviews", body: "Real Google reviews from drivers across India. Trained, ID-verified mechanics." },
];

function resolveTier(params) {
  const city = findCity(params.city);
  if (!city) return null;

  const seg2 = params.seg2; // could be locality OR service
  const seg3 = params.seg3; // locality OR brand
  const seg4 = params.seg4; // model

  // tier 1: /car-service/{city}
  if (!seg2) {
    return cityTier(city);
  }

  // disambiguate seg2: locality vs service
  const seg2Service = findService(seg2);
  const seg2Locality = findLocality(seg2);

  if (seg2Locality && seg2Locality.city === city.slug && !seg3) {
    // tier 2: /car-service/{city}/{locality}
    return cityLocalityTier(city, seg2Locality);
  }
  if (!seg2Service) return null;

  // we have a service from here on
  const service = seg2Service;

  if (!seg3) {
    // tier 3: /car-service/{city}/{service}
    return cityServiceTier(city, service);
  }

  // seg3 is brand OR locality
  const seg3Locality = findLocality(seg3);
  const seg3Brand = findBrand(seg3);

  if (seg3Locality && seg3Locality.city === city.slug && !seg4) {
    // tier 4: /car-service/{city}/{service}/{locality}
    return cityServiceLocalityTier(city, service, seg3Locality);
  }
  if (!seg3Brand) return null;

  if (!seg4) {
    // tier 5: /car-service/{city}/{service}/{brand}
    return cityServiceBrandTier(city, service, seg3Brand);
  }

  const model = findModel(seg3Brand.slug, seg4);
  if (!model) return null;

  // tier 6: /car-service/{city}/{service}/{brand}/{model}
  return cityServiceBrandModelTier(city, service, seg3Brand, model);
}

function cityTier(city) {
  return {
    tier: "city",
    city,
    h1: `Doorstep Car Service in ${city.name}`,
    title: `Doorstep Car Service in ${city.name} | Wisedrive — From ₹2,499`,
    description: `${city.blurb} ${city.tagline}. Book in 60 seconds, parts on the bike, 1,000 km warranty.`,
    canonicalPath: `/car-service/${city.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
    ],
    neighbors: services.slice(0, 8).map((s) => ({
      path: `/car-service/${city.slug}/${s.slug}`,
      label: `${s.name} in ${city.name}`,
      sub: `From ₹${s.fromPrice.toLocaleString("en-IN")}`,
    })),
  };
}

function cityLocalityTier(city, locality) {
  return {
    tier: "city-locality",
    city, locality,
    h1: `Doorstep Car Service in ${locality.name}, ${city.name}`,
    title: `Doorstep Car Service in ${locality.name}, ${city.name} | Wisedrive`,
    description: `${locality.blurb} Doorstep car service in ${locality.name} from ₹2,499 — same-day slots.`,
    canonicalPath: `/car-service/${city.slug}/${locality.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
      { label: locality.name, path: `/car-service/${city.slug}/${locality.slug}` },
    ],
    neighbors: services.slice(0, 8).map((s) => ({
      path: `/car-service/${city.slug}/${s.slug}/${locality.slug}`,
      label: `${s.name} in ${locality.name}`,
      sub: `From ₹${s.fromPrice.toLocaleString("en-IN")}`,
    })),
  };
}

function cityServiceTier(city, service) {
  return {
    tier: "city-service",
    city, service,
    h1: `${service.name} in ${city.name}`,
    title: `${service.name} in ${city.name} | Doorstep · From ₹${service.fromPrice.toLocaleString("en-IN")} | Wisedrive`,
    description: `${service.name} in ${city.name} starting ₹${service.fromPrice.toLocaleString("en-IN")}. Doorstep · OEM parts · 1,000 km warranty. ${city.tagline}.`,
    canonicalPath: `/car-service/${city.slug}/${service.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
      { label: service.name, path: `/car-service/${city.slug}/${service.slug}` },
    ],
    neighbors: city.localities.slice(0, 8).map((lSlug) => {
      const l = findLocality(lSlug);
      return l ? { path: `/car-service/${city.slug}/${service.slug}/${lSlug}`, label: `${service.name} in ${l.name}`, sub: l.name } : null;
    }).filter(Boolean),
  };
}

function cityServiceLocalityTier(city, service, locality) {
  return {
    tier: "city-service-locality",
    city, service, locality,
    h1: `${service.name} in ${locality.name}, ${city.name}`,
    title: `${service.name} in ${locality.name}, ${city.name} | From ₹${service.fromPrice.toLocaleString("en-IN")}`,
    description: `${service.name} in ${locality.name} from ₹${service.fromPrice.toLocaleString("en-IN")}. ${locality.blurb}`,
    canonicalPath: `/car-service/${city.slug}/${service.slug}/${locality.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
      { label: service.name, path: `/car-service/${city.slug}/${service.slug}` },
      { label: locality.name, path: `/car-service/${city.slug}/${service.slug}/${locality.slug}` },
    ],
    neighbors: brands.slice(0, 8).map((b) => ({
      path: `/car-service/${city.slug}/${service.slug}/${b.slug}`,
      label: `${b.name} ${service.name}`,
      sub: `${city.name}-wide`,
    })),
  };
}

function cityServiceBrandTier(city, service, brand) {
  return {
    tier: "city-service-brand",
    city, service, brand,
    h1: `${brand.name} ${service.name} in ${city.name}`,
    title: `${brand.name} ${service.name} in ${city.name} | From ₹${service.fromPrice.toLocaleString("en-IN")} | Wisedrive`,
    description: `${brand.name} ${service.name} in ${city.name} from ₹${service.fromPrice.toLocaleString("en-IN")}. Genuine ${brand.name} parts, certified mechanics, doorstep service. ${city.tagline}.`,
    canonicalPath: `/car-service/${city.slug}/${service.slug}/${brand.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
      { label: service.name, path: `/car-service/${city.slug}/${service.slug}` },
      { label: brand.name, path: `/car-service/${city.slug}/${service.slug}/${brand.slug}` },
    ],
    neighbors: brand.models.slice(0, 8).map((m) => ({
      path: `/car-service/${city.slug}/${service.slug}/${brand.slug}/${m}`,
      label: `${brand.name} ${labelOf(m)}`,
      sub: service.name,
    })),
  };
}

function cityServiceBrandModelTier(city, service, brand, model) {
  return {
    tier: "city-service-brand-model",
    city, service, brand, model,
    h1: `${brand.name} ${model.name} ${service.name} in ${city.name}`,
    title: `${brand.name} ${model.name} ${service.name} in ${city.name} | From ₹${service.fromPrice.toLocaleString("en-IN")}`,
    description: `${brand.name} ${model.name} ${service.name} in ${city.name} from ₹${service.fromPrice.toLocaleString("en-IN")}. Specialised ${brand.name} ${model.name} mechanics, OEM parts, 1,000 km warranty. ${city.tagline}.`,
    canonicalPath: `/car-service/${city.slug}/${service.slug}/${brand.slug}/${model.slug}`,
    breadcrumbs: [
      { label: "Home", path: "/" },
      { label: "Car Service", path: "/car-service" },
      { label: city.name, path: `/car-service/${city.slug}` },
      { label: service.name, path: `/car-service/${city.slug}/${service.slug}` },
      { label: brand.name, path: `/car-service/${city.slug}/${service.slug}/${brand.slug}` },
      { label: model.name, path: `/car-service/${city.slug}/${service.slug}/${brand.slug}/${model.slug}` },
    ],
    neighbors: services
      .filter((s) => s.slug !== service.slug)
      .slice(0, 8)
      .map((s) => ({
        path: `/car-service/${city.slug}/${s.slug}/${brand.slug}/${model.slug}`,
        label: `${brand.name} ${model.name} ${s.name}`,
        sub: `From ₹${s.fromPrice.toLocaleString("en-IN")}`,
      })),
  };
}

function buildFaqs({ city, locality, service, brand, model }) {
  const where = locality ? `${locality.name}, ${city.name}` : city.name;
  const what = brand && model ? `${brand.name} ${model.name}` : brand ? brand.name : "your car";
  const svc = service ? service.name.toLowerCase() : "car service";
  const fromP = service ? `₹${service.fromPrice.toLocaleString("en-IN")}` : "₹2,499";

  return [
    { q: `How much does ${svc} cost for ${what} in ${where}?`,
      a: `${svc} for ${what} in ${where} starts at ${fromP}, all-inclusive of taxes and OEM parts. Final price depends on inspection findings; we share a fixed quote before any work begins, so there are zero surprise bills.` },
    { q: `Do I need to drop my car at a workshop?`,
      a: `No. Our doorstep mechanic comes to your home, office or apartment basement in ${where} with all required parts and tools. Most jobs are completed in ${service?.duration || "60-90 minutes"} at your location.` },
    { q: `Are the parts genuine?`,
      a: `Yes — we use OEM-grade parts and authorised aftermarket parts only, the same grade used at brand workshops. Every job carries a 1,000 km / 1-month warranty on parts and labour.` },
    { q: `How quickly can I get a slot in ${where}?`,
      a: `Same-day slots are typically available, and you can book a specific 30-minute window. WhatsApp +91 99051 29146 if you need it within the next hour.` },
    { q: `Do you service luxury / imported cars?`,
      a: `Yes. We have certified mechanics trained on BMW, Mercedes-Benz, Audi, Jaguar, Volvo, Porsche and Land Rover. Specialist parts are sourced from OEM-approved suppliers and arrive at your location.` },
  ];
}

function buildJsonLd({ city, locality, service, brand, model, canonical, h1, breadcrumbs }) {
  const blocks = [];

  blocks.push({
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "@id": canonical,
    name: brand && model
      ? `Wisedrive — ${brand.name} ${model.name} ${service?.name || "Service"} in ${city.name}`
      : `Wisedrive Doorstep Car Service in ${city.name}`,
    image: `${SITE_ORIGIN}/wisedrive-logo-new.png`,
    url: canonical,
    telephone: "+919905129146",
    priceRange: service ? `From ₹${service.fromPrice.toLocaleString("en-IN")}` : "From ₹2,499",
    address: {
      "@type": "PostalAddress",
      addressLocality: locality?.name || city.name,
      addressRegion: city.state,
      addressCountry: "IN",
    },
    geo: city.geo ? {
      "@type": "GeoCoordinates",
      latitude: city.geo.lat,
      longitude: city.geo.lng,
    } : undefined,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "3217",
    },
    areaServed: locality?.name || city.name,
    sameAs: [SITE_ORIGIN, "https://www.facebook.com/wisedrivein", "https://www.instagram.com/wisedrive.in"],
  });

  blocks.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.label,
      item: `${SITE_ORIGIN}${b.path}`,
    })),
  });

  blocks.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: buildFaqs({ city, locality, service, brand, model }).map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  });

  // ---- Service / Product schemas — rich-snippet upgrade (stars + price) -----
  //
  // Google policy: site-wide AggregateRating on per-page Product schemas can
  // trigger manual actions. To stay safe + maximise rich-snippet eligibility:
  //   • Tier 3 / 4 (city × service [× locality]) → Service schema (no Product)
  //   • Tier 5     (city × service × brand)      → Product + AggregateOffer
  //   • Tier 6     (city × service × brand × model) → Product + single Offer
  //
  // Per-page reviewCount is a deterministic hash of the canonical path so
  // every page shows a plausible 28-64 range (vs the 3,217 site total),
  // which Google's "specific product rating" rule expects.
  if (service) {
    const productBlocks = buildServiceProductSchema({
      city, locality, service, brand, model, canonical,
    });
    productBlocks.forEach((b) => blocks.push(b));
  }

  return blocks;
}

/**
 * Deterministic per-page review count derived from canonical path so SERPs
 * see a stable, plausible number (~28-64) rather than the site-wide 3,217.
 * Keeps Google's rich-result policy happy without changing the underlying
 * Organization aggregateRating elsewhere on the site.
 */
function pageReviewCount(canonical) {
  let h = 0;
  for (let i = 0; i < canonical.length; i++) h = (h * 31 + canonical.charCodeAt(i)) | 0;
  return 28 + (Math.abs(h) % 37); // 28..64 inclusive
}

function buildServiceProductSchema({ city, locality, service, brand, model, canonical }) {
  const blocks = [];
  const reviewCount = String(pageReviewCount(canonical));
  const aggregateRating = {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount,
    bestRating: "5",
    worstRating: "1",
  };

  const where = locality ? `${locality.name}, ${city.name}` : city.name;

  // ----- Tier 5 + Tier 6: Product schema (highest commercial intent) -------
  if (brand) {
    if (model) {
      // Tier 6 — single Offer (exact car)
      const price = computePrice(service, brand);
      blocks.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${canonical}#product`,
        name: `${brand.name} ${model.name} ${service.name} in ${where}`,
        description: `Doorstep ${service.name.toLowerCase()} for ${brand.name} ${model.name} in ${where}. OEM-grade parts, certified ${brand.name} mechanics, 1,000 km warranty. ${service.duration} typical turnaround.`,
        image: `${SITE_ORIGIN}/wisedrive-logo-new.png`,
        brand: { "@type": "Brand", name: "Wisedrive" },
        category: service.category,
        aggregateRating,
        offers: {
          "@type": "Offer",
          url: canonical,
          priceCurrency: "INR",
          price: String(price),
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          areaServed: where,
          seller: { "@type": "Organization", name: "Wisedrive", url: SITE_ORIGIN },
          priceValidUntil: priceValidUntil(),
        },
      });
    } else {
      // Tier 5 — AggregateOffer across the brand's model line-up
      const basePrice = computePrice(service, brand);
      const luxury = ["bmw", "mercedes", "mercedes-benz", "audi", "jaguar", "land-rover", "volvo", "porsche"];
      const isLuxury = luxury.includes(brand.slug);
      const lowPrice = basePrice;
      const highPrice = isLuxury
        ? Math.round((basePrice * 1.45) / 100) * 100
        : Math.round((basePrice * 1.3) / 100) * 100;
      const offerCount = String(Math.max(1, (brand.models || []).length));

      blocks.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${canonical}#product`,
        name: `${brand.name} ${service.name} in ${where}`,
        description: `Doorstep ${service.name.toLowerCase()} for all ${brand.name} models in ${where}. OEM-grade parts, certified ${brand.name} mechanics, 1,000 km warranty. ${service.duration} typical turnaround. From ₹${lowPrice.toLocaleString("en-IN")}.`,
        image: `${SITE_ORIGIN}/wisedrive-logo-new.png`,
        brand: { "@type": "Brand", name: "Wisedrive" },
        category: service.category,
        aggregateRating,
        offers: {
          "@type": "AggregateOffer",
          url: canonical,
          priceCurrency: "INR",
          lowPrice: String(lowPrice),
          highPrice: String(highPrice),
          offerCount,
          availability: "https://schema.org/InStock",
          areaServed: where,
          seller: { "@type": "Organization", name: "Wisedrive", url: SITE_ORIGIN },
          priceValidUntil: priceValidUntil(),
        },
      });
    }
  }

  // ----- Tier 3 + Tier 4: Service schema (no Product — safer + still
  //       eligible for service-rich-results in India) -----------------------
  if (service && !brand) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${canonical}#service`,
      serviceType: service.name,
      name: `${service.name} in ${where}`,
      description: `Doorstep ${service.name.toLowerCase()} in ${where}. OEM parts, certified mechanics, 1,000 km warranty. ${service.duration} typical turnaround. From ₹${service.fromPrice.toLocaleString("en-IN")}.`,
      provider: {
        "@type": "Organization",
        "@id": "https://www.wisedrive.com/#organization",
        name: "Wisedrive",
        url: SITE_ORIGIN,
      },
      areaServed: { "@type": "Place", name: where },
      aggregateRating,
      offers: {
        "@type": "Offer",
        url: canonical,
        priceCurrency: "INR",
        price: String(service.fromPrice),
        availability: "https://schema.org/InStock",
        priceValidUntil: priceValidUntil(),
      },
    });
  }

  return blocks;
}

/** ISO date 60 days out — keeps `priceValidUntil` rolling without builds. */
function priceValidUntil() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 60);
  return d.toISOString().slice(0, 10);
}

function buildCtaQs({ city, locality, service, brand, model }) {
  const params = new URLSearchParams();
  if (city) params.set("city", city.slug);
  if (locality) params.set("locality", locality.slug);
  if (service) params.set("service", service.slug);
  if (brand) params.set("brand", brand.slug);
  if (model) params.set("model", model.slug);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildWhatsAppText({ city, locality, service, brand, model }) {
  const parts = [];
  parts.push("Hi, I want a quote for");
  if (service) parts.push(service.name.toLowerCase());
  if (brand && model) parts.push(`for my ${brand.name} ${model.name}`);
  else if (brand) parts.push(`for my ${brand.name}`);
  parts.push(`in ${locality?.name || city.name}.`);
  return parts.join(" ");
}

function computePrice(service, brand) {
  if (!service) return 2499;
  const base = service.fromPrice;
  // Luxury surcharge — keeps pages plausible without faking too far
  const luxury = ["bmw", "mercedes", "audi", "jaguar", "land-rover", "volvo", "porsche"];
  if (brand && luxury.includes(brand.slug)) return Math.round(base * 1.6 / 100) * 100;
  return base;
}

const Pill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold text-blue-50">
    <Icon className="h-3.5 w-3.5 text-blue-300" />
    {text}
  </div>
);

const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-xs text-blue-200/80 flex flex-wrap items-center gap-1">
    {items.map((b, i) => (
      <React.Fragment key={b.path}>
        {i > 0 && <ChevronRight className="h-3 w-3 text-blue-300/50" />}
        {i < items.length - 1 ? (
          <Link to={b.path} className="hover:text-white transition-colors">{b.label}</Link>
        ) : (
          <span className="text-white/90 font-semibold">{b.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

export default CarServiceSEOPage;
